import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import ComponentPalette from './components/ComponentPalette';
import ReportCanvas from './components/ReportCanvas';
import DataSourcePanel from './components/DataSourcePanel';
import AITextGenerator from './components/AITextGenerator';
import PublishingControls from './components/PublishingControls';

const CustomReportBuilder = () => {
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [selectedDataSources, setSelectedDataSources] = useState([]);
  const [reportTitle, setReportTitle] = useState('New Custom Report');
  const [showAIPanel, setShowAIPanel] = useState(false);

  const availableComponents = [
    {
      id: 'data-table',
      name: 'Data Table',
      icon: 'Table',
      description: 'Display data in tabular format with sorting and filtering',
      category: 'data',
    },
    {
      id: 'bar-chart',
      name: 'Bar Chart',
      icon: 'BarChart3',
      description: 'Visualize comparative data with vertical or horizontal bars',
      category: 'charts',
    },
    {
      id: 'line-chart',
      name: 'Line Chart',
      icon: 'TrendingUp',
      description: 'Show trends and patterns over time',
      category: 'charts',
    },
    {
      id: 'pie-chart',
      name: 'Pie Chart',
      icon: 'PieChart',
      description: 'Display proportional data distribution',
      category: 'charts',
    },
    {
      id: 'metrics-widget',
      name: 'Metrics Widget',
      icon: 'Activity',
      description: 'Highlight key performance indicators',
      category: 'metrics',
    },
    {
      id: 'text-block',
      name: 'Text Block',
      icon: 'Type',
      description: 'Add formatted text content and descriptions',
      category: 'content',
    },
    {
      id: 'compliance-checklist',
      name: 'Compliance Checklist',
      icon: 'CheckSquare',
      description: 'Display compliance status and requirements',
      category: 'compliance',
    },
    {
      id: 'timeline',
      name: 'Timeline',
      icon: 'Clock',
      description: 'Visualize events and milestones chronologically',
      category: 'data',
    },
  ];

  const dataSources = [
    {
      id: 'incidents',
      name: 'Incident Management',
      icon: 'AlertTriangle',
      fields: ['incident_id', 'date', 'severity', 'location', 'status'],
      description: 'All incident records and investigation data',
    },
    {
      id: 'audits',
      name: 'Safety Audits',
      icon: 'ClipboardCheck',
      fields: ['audit_id', 'audit_date', 'score', 'findings', 'location'],
      description: 'Audit records and compliance scores',
    },
    {
      id: 'training',
      name: 'Training Records',
      icon: 'GraduationCap',
      fields: ['training_id', 'personnel_name', 'course', 'completion_date', 'status'],
      description: 'Training completion and certification data',
    },
    {
      id: 'compliance',
      name: 'Compliance Data',
      icon: 'Shield',
      fields: ['compliance_id', 'requirement', 'status', 'due_date', 'location'],
      description: 'Regulatory compliance tracking',
    },
    {
      id: 'work-permits',
      name: 'Work Permits',
      icon: 'FileText',
      fields: ['permit_id', 'permit_type', 'status', 'location', 'issued_date'],
      description: 'Work permit issuance and tracking',
    },
  ];

  const handleAddComponent = (component) => {
    const newComponent = {
      ...component,
      instanceId: `${component?.id}-${Date.now()}`,
      position: selectedComponents?.length,
      config: {},
    };
    setSelectedComponents([...selectedComponents, newComponent]);
  };

  const handleRemoveComponent = (instanceId) => {
    setSelectedComponents(selectedComponents?.filter((c) => c?.instanceId !== instanceId));
  };

  const handleReorderComponents = (fromIndex, toIndex) => {
    const items = Array.from(selectedComponents);
    const [reorderedItem] = items?.splice(fromIndex, 1);
    items?.splice(toIndex, 0, reorderedItem);
    setSelectedComponents(items);
  };

  const handleToggleDataSource = (source) => {
    const isSelected = selectedDataSources?.some((s) => s?.id === source?.id);
    if (isSelected) {
      setSelectedDataSources(selectedDataSources?.filter((s) => s?.id !== source?.id));
    } else {
      setSelectedDataSources([...selectedDataSources, source]);
    }
  };

  const handleInsertAIText = (text, targetComponentId) => {
    setSelectedComponents(
      selectedComponents?.map((component) => {
        if (component?.instanceId === targetComponentId) {
          return {
            ...component,
            config: {
              ...component?.config,
              content: text,
            },
          };
        }
        return component;
      })
    );
  };

  const handlePublish = (publishConfig) => {
    console.log('Publishing report:', reportTitle, publishConfig);
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <input
            type="text"
            value={reportTitle}
            onChange={(e) => setReportTitle(e?.target?.value)}
            className="text-3xl font-bold text-foreground bg-transparent border-none focus:outline-none"
            placeholder="Report Title"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                showAIPanel
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name="Sparkles" size={16} />
              AI Assistant
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium">
              <Icon name="Eye" size={16} />
              Preview
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
              <Icon name="Save" size={16} />
              Save Draft
            </button>
          </div>
        </div>
        <p className="text-muted-foreground">
          Build custom reports with drag-and-drop components, data source integration, and AI-powered text generation
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="space-y-6">
          <ComponentPalette
            components={availableComponents}
            onAddComponent={handleAddComponent}
          />
          <DataSourcePanel
            dataSources={dataSources}
            selectedSources={selectedDataSources}
            onToggleSource={handleToggleDataSource}
          />
        </div>

        <div className={showAIPanel ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <ReportCanvas
            components={selectedComponents}
            onRemoveComponent={handleRemoveComponent}
            onReorderComponents={handleReorderComponents}
            dataSources={selectedDataSources}
          />
        </div>

        {showAIPanel && (
          <div>
            <AITextGenerator
              onInsertText={handleInsertAIText}
              textComponents={selectedComponents?.filter((c) => c?.id === 'text-block')}
            />
          </div>
        )}
      </div>
      <div className="mt-6">
        <PublishingControls onPublish={handlePublish} />
      </div>
    </div>
  );
};

export default CustomReportBuilder;